import {TableCellProps} from '@material-ui/core/TableCell';
import {Map} from 'immutable';
import {createStandardAction} from 'typesafe-actions';
import {SortProps} from '../consts';
import {IndexRange} from 'react-virtualized';
import * as React from 'react';
import * as _ from 'lodash';
import {AlertProps} from '@material-ui/core/Alert';
import {ValueType} from 'cl-form/validation/validators';
import {FieldInfo, FieldsInfo} from 'cl-form/FormSections/consts';
import {FieldType} from 'cl-form/TypedFormField';
import {SELECTION_NONE} from './reducer';
import update from 'immutability-helper';

export enum NumericOperator {
  GTE = '>=',
  GT = '>',
  LT = '<',
  LTE = '<=',
  EQL = '=',
  NEQ = 'Not equals',
}

export enum StringOperator {
  CONTAINS = 'Contains',
  NOT_CONTAINS = 'Does not contain',
  EQUALS = 'Equals',
  NOT_EQUALS = 'Not equals',
  STARTS_WITH = 'Starts with',
}

export enum ObjectOperator {
  KEY_EXISTS = 'Key exists',
}

export enum SpecialOperator {
  TERMS_LOOKUP = 'from',
}

export enum UniversalOperator { // Operator that is applicable to all data types.
  NON_EMPTY = 'Not empty',
  EMPTY = 'Empty',
}

export type OperatorType = StringOperator | NumericOperator | ObjectOperator | SpecialOperator | UniversalOperator;

export type RowDataType<Columns extends string = string> = Record<Columns, ValueType>;

export interface ColumnActionsProps {
  tableName: string;
  columnName: string;
}

export type ColumnType = FieldType;

export interface ColumnsInfo extends FieldInfo<RowDataType> {
  filterRequired?: boolean;
  filterRecommended?: boolean;
  filterDisabled?: boolean;
  onFilterDelete?: (filterValues: FilterValuesType) => void;
  onFilterClick?: () => void;
  actionComponent?: React.ComponentType<ColumnActionsProps>;
  filterReadonly?: boolean;
  getHref?: (row: RowDataType) => string;
  renderRowHoverActions?: (row: RowDataType, className: string) => React.ReactNode;
  color?: string;
  children?: string[];
  fields?: {keyword?: any};
  align?: TableCellProps['align'];
  mappingDisabled?: boolean;
  childField?: boolean;
  icon?: (row: RowDataType) => React.ReactElement<any>;
  iconOnly?: boolean;
  sticky?: boolean;
  sortDisabled?: boolean;
  composite?: {[rowDataKey: string]: string};
}

export type ComplexCondition =
  | {operator: NumericOperator; value: number}
  | {operator: StringOperator; value: string}
  | {operator: SpecialOperator.TERMS_LOOKUP; index: string; id: string; path: string; value: string}
  | {operator: UniversalOperator; value?: ValueType}
  | {operator: ObjectOperator.KEY_EXISTS; value: object[]};

export type FilterValueType = ValueType | ComplexCondition;

export interface FilterValuesType {
  [fieldName: string]: FilterValueType;
}

export type Selection = {
  include?: ValueType[];
  exclude?: ValueType[];
};

export type ColumnsType = {[name: string]: ColumnsInfo};
export type VisibleColumnsType = string[];
export type VisibleColumnsPresetsType = {[name: string]: VisibleColumnsType};
export type ColumnsGroups = VisibleColumnsPresetsType;

export type RowStatus = {icon: React.ReactNode; color: AlertProps['color']; error?: boolean};

export const ROW_CHILDREN_INDICES = '_childrenIndices';
export const ROW_INDEX = '_index';
export const ROW_PARENT_INDEX = '_parentIndex';

export class ColumnsConfig<RowData extends RowDataType = RowDataType> {
  thumbnail?: keyof RowData;
  title?: keyof RowData;
  id?: keyof RowData;
  columnsSorter?: (columns) => string[];
  status?(rowData: RowData, tableState: TableState<RowData>): RowStatus;
  parentId?: string;
  constructor(config?: Partial<ColumnsConfig<RowData>>) {
    _.assign(this, _.mapKeys(['thumbnail', 'title', 'id', 'parentId']), config);
  }
}

export interface TableState<RowData extends RowDataType = RowDataType> {
  columnsConfig: ColumnsConfig<RowData>;
  initParams?: any;
  data: Map<number, RowData>;
  dataUpdates?: Map<number, RowData>;
  columns?: ColumnsType;
  visibleColumns?: VisibleColumnsType;
  count: null | number;
  selection: Selection;
  sortProps?: SortProps;
  visibleRange?: IndexRange;
  tableRefresh?: boolean;
  filter?: FilterValuesType;
  filterOpen?: boolean;
  filterRenderButtons?: boolean;
  additionalData?: any;
  virtualized: boolean;
}

export type TablesState<RowData extends RowDataType = RowDataType> = {
  [tableName: string]: TableState<RowData>;
};

export const setInitialTableState = createStandardAction('@reduxTable/SET_INITIAL_TABLE_STATE')<{
  name: string;
  state: Partial<TableState>;
  showAllVisibleColumns: boolean;
}>();
export const deleteTable = createStandardAction('@reduxTable/DELETE_TABLE')<{name: string}>();
export const setRows = createStandardAction('@reduxTable/SET_ROWS')<{
  name: string;
  startIndex: number;
  rows: RowDataType[];
  reset?: boolean;
  count?: number;
  additionalData?: any;
  supportChildren?: boolean;
}>();
export const updateRows = createStandardAction('@reduxTable/UPDATE_ROWS')<{
  name: string;
  selection?: Selection;
  columnName: string;
  updater: (row: RowDataType) => ValueType;
}>(); // if no selection is specified, current selection is used.
export const setSortProps = createStandardAction('@reduxTable/SET_SORT_PROPS')<{
  name: string;
  sortProps: SortProps;
}>();
export const setVisibleRange = createStandardAction('@reduxTable/VISIBLE_RANGE')<{
  name: string;
  visibleRange: IndexRange;
}>();
export const triggerTableRefresh = createStandardAction('@reduxTable/TRIGGER_TABLE_REFRESH')<{name: string}>();
export const clearTableRefresh = createStandardAction('@reduxTable/CLEAR_TABLE_REFRESH')<{name: string}>();
export const toggleSelectAll = createStandardAction('@reduxTable/TOGGLE_SELECT_ALL')<{name: string}>();
export const toggleSelect = createStandardAction('@reduxTable/TOGGLE_SELECT')<{
  name: string;
  index: number;
}>();
export const setFilter = createStandardAction('@reduxTable/SET_FILTER')<{name: string; filter: FilterValuesType}>();
export const clearFilter = createStandardAction('@reduxTable/CLEAR_FILTER')<{name: string}>();
export const setVisibleColumns = createStandardAction('@reduxTable/SET_VISIBLE_COLUMNS')<{
  name: string;
  visibleColumns: VisibleColumnsType;
}>();
export const setFilterOpen = createStandardAction('@reduxTable/SET_FILTER_OPEN')<{name: string; open: boolean}>();
export const setAdditionalData = createStandardAction('@reduxTable/SET_ADDITIONAL_DATA')<{
  name: string;
  key: string;
  value: any;
}>();
export const adjustSelection = createStandardAction('@reduxTable/ADJUST_SELECTION')<{
  name: string;
  ids: string[];
  count: number;
}>();
export const setData = createStandardAction('@reduxTable/SET_DATA')<{
  name: string;
  data: Map<number, RowDataType>;
}>();
export const setColumns = createStandardAction('@reduxTable/SET_COLUMNS')<{
  name: string;
  columns: ColumnsType;
  resetVisibleColumns?: boolean;
}>();

export function isSelected(
  tableState: Pick<TableState<any>, 'selection' | 'data' | 'columnsConfig'>,
  index: number
): boolean {
  if (!tableState.selection) {
    return true;
  }
  const rowData = tableState.data.get(index);
  const idColumnName = tableState.columnsConfig.id;
  if (!rowData) {
    return false;
  }
  const id: ValueType = idColumnName ? rowData[idColumnName] : index;
  return tableState.selection.include
    ? tableState.selection.include.indexOf(id) !== -1
    : tableState.selection.exclude.indexOf(id) === -1;
}

export function selectedCount(tableState: Pick<TableState, 'count' | 'selection'>): number {
  const {
    selection: {exclude, include},
    count,
  } = tableState;
  return include ? include.length : count - exclude.length;
}

export function isAnySelected(tableState: Pick<TableState, 'count' | 'selection'>) {
  return selectedCount(tableState) > 0;
}

export function isAllSelected(tableState: Pick<TableState, 'count' | 'selection'>) {
  return tableState.count === selectedCount(tableState);
}

export function adjustSelectionHelper(state, name, idsToKeep, count) {
  // adjust selection for deleted rows.
  return update(state, {
    [name]: {
      selection: {
        $apply: (selection: TableState['selection']) => {
          if (selection.include) {
            return {
              include: _.intersection(selection.include, idsToKeep),
            };
          } else if (count === 0) {
            return SELECTION_NONE;
          }
          return selection;
        },
      },
    },
  });
}

export function columnsInfoToFieldsInfo(columnsInfo: ColumnsType): FieldsInfo<any> {
  return _.map(columnsInfo, (info, name: string) => ({...info, name}));
}

export const removeMetadataFields = object => _.omitBy(object, (value, name) => _.startsWith(name, '_'));
